import type {
  Blend,
  Formula,
  Ingredient,
} from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';

import { Skeleton } from '@design-system/Skeleton';
import { MyIngredientsCard } from '@design-system/Cards/MyIngredientsCard';

export interface MyIngredientsPageFormulaProps {
  name?: string;
  formula?: Formula;
  productId?: number;
  isLoading?: boolean;
  isSubProduct?: boolean;
  hideBookmark?: boolean;
  disableLink?: boolean;
}

const renderIngredients = (
  ingredients: Ingredient[],
  productId: number,
  hideBookmark?: boolean,
  disableLink?: boolean,
) => {
  const renderedIngredients = ingredients.map((ingredient) => (
    <li key={ingredient.id}>
      <MyIngredientsCard
        ingredient={ingredient}
        productId={productId}
        hideBookmark={hideBookmark}
        disableLink={disableLink}
      />
    </li>
  ));

  return renderedIngredients;
};

const renderBlends = (
  blends: Blend[],
  productId: number,
  hideBookmark?: boolean,
  disableLink?: boolean,
) => {
  const renderedBlends = blends.map((blend) => {
    if (!blend.ingredients?.length) return null;

    return (
      <div key={blend.name} className="flex flex-col gap-4">
        <span className="typography-title text-tertiary">{blend.name}</span>

        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {renderIngredients(
            blend.ingredients,
            productId,
            hideBookmark,
            disableLink,
          )}
        </ul>
      </div>
    );
  });

  return renderedBlends;
};

export const MyIngredientsPageFormula = ({
  name,
  formula,
  productId,
  isLoading,
  isSubProduct,
  hideBookmark,
  disableLink,
}: MyIngredientsPageFormulaProps) => {
  const ingredients = formula?.ingredients ?? [];
  const blends = formula?.blends ?? [];
  const hasResults = ingredients.length > 0 || blends.length > 0;

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {Array.from({ length: 10 }).map((_, index) => (
          <Skeleton key={index} className="w-full h-24" />
        ))}
      </div>
    );
  }

  if (!hasResults || typeof productId !== 'number') {
    return (
      <div className="flex flex-col gap-4 py-14 text-center">
        <span className="typography-h3 text-primary">No results found</span>

        <span className="typography-body text-secondary">
          Try adjusting your filters to see more results
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {isSubProduct && name && <span className="typography-h1">{name}</span>}

      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {renderIngredients(ingredients, productId, hideBookmark, disableLink)}
      </ul>

      {renderBlends(blends, productId, hideBookmark, disableLink)}
    </div>
  );
};
